import map from 'lodash/map';
import debounce from 'lodash/debounce';
import UserDatabases from '@Services/UserDatabases';
import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import namespacedCache from '@Services/NamespacedCache';

import { RouteType } from '@Pages/RetroSynthesis/constants';

import { runSearch } from '@Pages/RetroSynthesis/ProjectRoute/ReaxysExamplesSearchService';
import { noCache } from '@Utils/Network';
import { showCopiedToastMessage } from '@Containers/ToastMessages/CopiedToastMessage/CopiedToastMessageAction';
import { RsRoute } from '@Pages/RetroSynthesis/routes';
import {
  PROJECT_ACCESS_FAILURE_ERROR_NUMBER,
  PROJECT_ACCESS_FAILURE_STATUS_CODE,
} from '@Pages/RetroSynthesis/RetroSynthesisProjects/constants';
import { projectRoutesCacheKey } from '@Pages/RetroSynthesis/ProjectRoutes/constants';
import { fetchProjectBreadcrumbInfoAction } from '@Pages/RetroSynthesis/RsBreadcrumbs/actions';
import { buildConditionsForStep, buildRequestPayloadForExamplesSearch } from './utils/conditionsDataBuilder';
import {
  FETCH_PROJECT_ROUTE_DATA,
  FETCH_PROJECT_ROUTE_DATA_SUCCESS,
  FETCH_PROJECT_ROUTE_DATA_FAIL,
  PROJECT_ROUTE_SET_ACTIVE_STEP,
  PROJECT_ROUTE_CONDITIONS_FETCH_STEP_DATA,
  PROJECT_ROUTE_CONDITIONS_STEP_DATA_SUCCESS,
  PROJECT_ROUTE_CONDITIONS_STEP_DATA_ERROR,
  PROJECT_ROUTE_CONDITIONS_SET_PLACEMENT,
  PROJECT_ROUTE_CONDITIONS_SET_IS_SHOWN,
  STEP_DATA_EXAMPLES_SEARCH_DEBOUNCE_DELAY,
  projectRouteDataCacheKey,
  COPY_ROUTE_FAILURE,
  PROJECT_ROUTE_SET_IS_TRANSFER_FROM_REACTION,
  PROJECT_ROUTE_CONDITIONS_STEP_SELECTION_SAVED,
  DELETE_STEPS,
  DELETE_STEPS_SUCCESS,
  DELETE_STEPS_FAILURE,
  PROJECT_ROUTE_EXAMPLES_STEP_DATA_SUCCESS,
  CLEAR_DELETED_ROUTE,
} from './constants';

export const fetchData = withGlobalSpinnerActionEnhancer(({ projectId, routeId, history, cache = true }) => async (dispatch) => {
  Logger.info('+++ RT Project Route - fetch data +++', { projectId, routeId });
  dispatch({
    type: FETCH_PROJECT_ROUTE_DATA,
    payload: { projectId, routeId },
  });
  try {
    const cacheKey = projectRouteDataCacheKey(projectId, routeId);
    let result = null;
    if (cache) {
      result = namespacedCache.getByKey(cacheKey);
    }
    if (!result) {
      result = await MessageBroker.send(RequestUrls.rsProjectRoute, { projectId, routeId }, noCache());
      namespacedCache.setByKey(cacheKey, result);
    }

    Logger.info('+++ RT Project Route - fetch data success +++', result);
    dispatch({
      type: FETCH_PROJECT_ROUTE_DATA_SUCCESS,
      payload: result,
    });
  } catch (e) {
    Logger.info('+++ RT Project Route - fetch data error +++', e);
    dispatch({
      type: FETCH_PROJECT_ROUTE_DATA_FAIL,
      payload: e,
    });
    if (e.errorNumber === PROJECT_ACCESS_FAILURE_ERROR_NUMBER && e.statusCode === PROJECT_ACCESS_FAILURE_STATUS_CODE) {
      // project access error - goto project page
      history.push(RsRoute.PROJECTS_LIST);
    }
  }
});

export const setActiveStep = (activeStep) => ({
  type: PROJECT_ROUTE_SET_ACTIVE_STEP,
  payload: { activeStep },
});

export const setIsTransferFromReaction = (isTransferFromReaction) => ({
  type: PROJECT_ROUTE_SET_IS_TRANSFER_FROM_REACTION,
  payload: { isTransferFromReaction },
});

export const fetchPublishedStepData =
  ({ projectId, routeId, step, databaseId, reactionId }) =>
  async (dispatch) => {
    dispatch({
      type: PROJECT_ROUTE_CONDITIONS_FETCH_STEP_DATA,
      payload: { step },
    });
    try {
      const response = await MessageBroker.send(
        RequestUrls.getPlannerReactionsConditions,
        {
          projectId,
          routeId,
          step,
        },
        {},
        {
          [databaseId]: [reactionId],
        }
      );
      // Map to make it work with SynthesisPlannerConditionsComponent
      const data = buildConditionsForStep(response, step);
      dispatch({
        type: PROJECT_ROUTE_CONDITIONS_STEP_DATA_SUCCESS,
        payload: { step, data },
      });
    } catch (error) {
      dispatch({
        type: PROJECT_ROUTE_CONDITIONS_STEP_DATA_ERROR,
        payload: { step, error },
      });
    }
  };

let stepDataSearchCancel;

const fetchPredictedStepDataDebounced = debounce(
  async (step, examples, firstPriority, dispatch) => {
    dispatch({
      type: PROJECT_ROUTE_CONDITIONS_FETCH_STEP_DATA,
      payload: { step },
    });

    try {
      const databaseId = UserDatabases.getDefaultDatabase().id;
      const reactionIds = map(examples, ({ reactionId }) => reactionId);

      // run a new search
      const { promise: searchPromise, cancel: searchCancel } = runSearch(
        buildRequestPayloadForExamplesSearch({
          reactionIds,
          databaseId,
        })
      );

      // save cancel callback
      stepDataSearchCancel = searchCancel;

      // await for search result
      const resultItem = await searchPromise;

      // if everything ok - remove cancel callback
      stepDataSearchCancel = undefined;

      dispatch({
        type: PROJECT_ROUTE_EXAMPLES_STEP_DATA_SUCCESS,
        payload: { step, firstPriority, data: { ...resultItem, reactionIds } },
      });
    } catch (error) {
      // if something went wrong - cancel search
      // eslint-disable-next-line no-unused-expressions
      stepDataSearchCancel && stepDataSearchCancel();

      dispatch({
        type: PROJECT_ROUTE_CONDITIONS_STEP_DATA_ERROR,
        payload: { step, error },
      });
    }
  },
  STEP_DATA_EXAMPLES_SEARCH_DEBOUNCE_DELAY,
  { leading: true, trailing: true }
);

const firstPriorityExamplesCount = 5;
export const fetchPredictedTopStepData = (stepData) => async (dispatch) => {
  // cancel previous search if running
  if (stepDataSearchCancel) {
    stepDataSearchCancel();
    stepDataSearchCancel = undefined;
  }
  const { step, examples, selected } = stepData;
  let firstExamples;
  if (selected) {
    // if view selected enabled load top 5 visible selected examples
    firstExamples = examples.filter((e) => e.selected).slice(0, firstPriorityExamplesCount);
  } else {
    // just load first 5 from the list
    firstExamples = examples.slice(0, firstPriorityExamplesCount);
  }
  fetchPredictedStepDataDebounced(step, firstExamples, true, dispatch);
};

export const fetchPredictedLastStepData = (stepData) => async (dispatch) => {
  // cancel previous search if running
  if (stepDataSearchCancel) {
    stepDataSearchCancel();
    stepDataSearchCancel = undefined;
  }
  const { step, examples, selected } = stepData;
  let restExamples;
  if (selected) {
    // if view selected enabled load rest except 5 visible selected examples
    const firstExamples = examples.filter((e) => e.selected).slice(0, firstPriorityExamplesCount);
    restExamples = examples.filter((e) => !firstExamples.some((fe) => e.reactionId === fe.reactionId));
  } else {
    // just load rest from the list
    restExamples = examples.slice(firstPriorityExamplesCount);
  }
  if (restExamples.length) {
    fetchPredictedStepDataDebounced(step, restExamples, false, dispatch);
  }
};

export const fetchConditionsStepData =
  (type, stepData) =>
  // eslint-disable-next-line consistent-return
  (dispatch) => {
    switch (type) {
      case RouteType.PUBLISHED: {
        return dispatch(fetchPublishedStepData(stepData));
      }
      case RouteType.PREDICTED: {
        return dispatch(fetchPredictedTopStepData(stepData));
      }
      default: {
        Logger.error('Invalid type to fetch conditions step data', type, stepData);
      }
    }
  };

export const onConditionsUnmount = () => () => {
  if (stepDataSearchCancel) {
    stepDataSearchCancel();
  }
};

export const setConditionsPlacement = (placement) => ({
  type: PROJECT_ROUTE_CONDITIONS_SET_PLACEMENT,
  payload: { placement },
});

export const setConditionsIsShown = (isShown) => ({
  type: PROJECT_ROUTE_CONDITIONS_SET_IS_SHOWN,
  payload: { isShown },
});

export const copyRoute = (projectId, routeId) => async (dispatch) => {
  try {
    const response = await MessageBroker.send(RequestUrls.rsProjectsExportRoute, { projectId, routeId }, noCache());
    navigator.clipboard.writeText(response);
    dispatch(showCopiedToastMessage('You can now paste the route in the \n structure editor of your choice.'));
  } catch (error) {
    dispatch({ type: COPY_ROUTE_FAILURE, payload: error });
  }
};

export const saveStepSelections =
  ({ projectId, routeId, stepId, selected, selectedIds }) =>
  async (dispatch) => {
    try {
      const response = await MessageBroker.send(
        RequestUrls.rsProjectsSaveStepSelections,
        { projectId, routeId, stepId, selected, selectedIds },
        noCache()
      );

      // update route cache
      const cacheKey = projectRouteDataCacheKey(projectId, routeId);
      const cachedRoute = namespacedCache.getByKey(cacheKey);
      if (cachedRoute) {
        namespacedCache.setByKey(cacheKey, {
          ...cachedRoute,
          // eslint-disable-next-line no-use-before-define
          routeLayout: _getSyncedRouteLayout(cachedRoute.routeLayout, stepId, selected, selectedIds),
        });
      }
      dispatch({
        type: PROJECT_ROUTE_CONDITIONS_STEP_SELECTION_SAVED,
        payload: response,
      });
    } catch (error) {
      Logger.error('RT Steps selection saving failed', error);
    }
  };

const _getSyncedRouteLayout = (routeLayout, stepId, selected, selectedIds) => {
  // customized route with groups
  if (routeLayout.some((r) => r.groups && r.groups.some((g) => g.id === stepId))) {
    const selectedRouteLayout = routeLayout.find((r) => r.groups && r.groups.some((g) => g.id === stepId));
    const updatedRouteLayout = {
      ...selectedRouteLayout,
      groups: selectedRouteLayout.groups.map((g) => ({
        ...g,
        selected: g.id === stepId ? selected : g.selected,
      })),
      examples: selectedRouteLayout.examples
        ? selectedRouteLayout.examples.map((e) => ({ ...e, selected: selectedIds.includes(e.reactionId) ? true : e.selected }))
        : undefined,
    };
    return routeLayout.map((rl) => (rl.id === selectedRouteLayout.id ? updatedRouteLayout : rl));
  }
  // published or predicted
  return routeLayout.map((rl) => ({
    ...rl,
    selected: rl.step === stepId ? selected : rl.selected,
    examples: rl.examples
      ? rl.examples.map((e) => ({ ...e, selected: selectedIds.includes(e.reactionId) ? true : e.selected }))
      : undefined,
  }));
};

export const deleteRouteSteps = (projectId, routeId, step) => async (dispatch) => {
  dispatch({ type: DELETE_STEPS });
  try {
    const response = await MessageBroker.send(RequestUrls.rsProjectsDeleteRouteSteps, { projectId, routeId, step }, noCache());
    namespacedCache.removeByKey(projectRouteDataCacheKey(projectId, routeId));
    namespacedCache.removeByKey(projectRoutesCacheKey(projectId));
    // Refresh customizedRoutesCount in RsBreadcrumbs
    dispatch(fetchProjectBreadcrumbInfoAction(projectId));
    dispatch({ type: DELETE_STEPS_SUCCESS, payload: response });
  } catch (error) {
    dispatch({ type: DELETE_STEPS_FAILURE, payload: error });
  }
};

export const clearDeletedRoute = () => ({ type: CLEAR_DELETED_ROUTE });
